<template>
  <div class="signature-capture-container rounded-lg">
    <div class="signature-header">
      <h2>{{ title }}</h2>
      <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
    </div>

    <div class="sign-here-label">
      <span class="sign-here-text">SIGN HERE</span>
      <i class="pi pi-chevron-down chevron-icon"></i>
    </div>
    <div class="signature-pad-container bg-gray-200">
      <VueSignaturePad ref="signaturePad" :width="width" :height="height" :options="options" />
    </div>

    <div class="signature-actions">
      <div class="action-left">
        <div v-if="legalDeclaration" class="agreement-text">
          By signing this, I agree to the following statement.
        </div>
      </div>
      <div class="action-right">
        <Button label="Clear" icon="pi pi-refresh" class="p-button-outlined mr-2" @click="clear" />
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-outlined p-button-danger mr-2"
          @click="cancel"
        />
        <Button label="Save Signature" icon="pi pi-check" @click="save" />
      </div>
    </div>

    <!-- Legal Declaration Section -->
    <div v-if="legalDeclaration" class="legal-declaration">
      <div class="declaration-text">{{ legalDeclaration }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { VueSignaturePad, type SignaturePadMethods } from 'vue-signature-pad'
import Button from 'primevue/button'

const props = defineProps({
  title: {
    type: String,
    default: 'Please Sign Below',
  },
  subtitle: {
    type: String,
    default: '',
  },
  width: {
    type: String,
    default: '100%',
  },
  height: {
    type: String,
    default: '300px',
  },
  legalDeclaration: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits(['save', 'cancel'])

const signaturePad = ref<(InstanceType<typeof VueSignaturePad> & SignaturePadMethods) | null>(null)

const options = {
  penColor: 'rgb(0, 0, 0)',
  backgroundColor: 'rgb(248, 249, 250)',
  minWidth: 1,
  maxWidth: 2.5,
  velocityFilterWeight: 0.7,
}

function clear() {
  signaturePad.value?.clearSignature()
}

function cancel() {
  emit('cancel')
}

async function save() {
  if (!signaturePad.value) return

  const { isEmpty, data } = signaturePad.value.saveSignature()

  if (isEmpty) {
    alert('Please provide a signature')
    return
  }

  try {
    // Process the signature to ensure proper aspect ratio
    const processedSignature = await processSignature(data)

    emit('save', {
      dataUrl: processedSignature,
      timestamp: new Date(),
      declarationAgreed: true, // Signing implies agreement
    })
  } catch (error) {
    console.error('Error processing signature:', error)
  }
}

// Process signature to ensure proper aspect ratio and trim whitespace
function processSignature(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      // First, create a temporary canvas to analyze the image and find content bounds
      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')

      if (!tempCtx) {
        throw new Error('Failed to get 2d context')
      }

      tempCanvas.width = img.width
      tempCanvas.height = img.height
      tempCtx.drawImage(img, 0, 0)

      // Get image data to analyze pixels
      const imageData = tempCtx.getImageData(0, 0, img.width, img.height)
      const data = imageData.data

      // Find the bounds of signature content (dark pixels)
      let minX = img.width
      let minY = img.height
      let maxX = 0
      let maxY = 0

      // Look for dark pixels (signature ink) - pixels that are significantly darker than white/light gray
      // Calculate brightness and consider pixels darker than a threshold as signature content
      const brightnessThreshold = 200 // Pixels darker than this are considered signature content

      for (let y = 0; y < img.height; y++) {
        for (let x = 0; x < img.width; x++) {
          const idx = (y * img.width + x) * 4
          const r = data[idx]
          const g = data[idx + 1]
          const b = data[idx + 2]
          const a = data[idx + 3]

          // Skip transparent pixels
          if (a === 0) continue

          // Calculate brightness (weighted average)
          const brightness = r * 0.299 + g * 0.587 + b * 0.114

          // If pixel is dark enough, it's part of the signature
          if (brightness < brightnessThreshold) {
            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            maxX = Math.max(maxX, x)
            maxY = Math.max(maxY, y)
          }
        }
      }

      // Add some padding around the signature
      const padding = 10
      minX = Math.max(0, minX - padding)
      minY = Math.max(0, minY - padding)
      maxX = Math.min(img.width, maxX + padding)
      maxY = Math.min(img.height, maxY + padding)

      const croppedWidth = maxX - minX
      const croppedHeight = maxY - minY

      // If no content found, use the original image
      if (croppedWidth <= 0 || croppedHeight <= 0) {
        minX = 0
        minY = 0
        maxX = img.width
        maxY = img.height
      }

      // Create the final canvas with dimensions matching the cropped signature
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error('Failed to get 2d context')
      }

      // Set canvas to exact size of cropped signature
      canvas.width = croppedWidth
      canvas.height = croppedHeight

      // Fill with white background
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, croppedWidth, croppedHeight)

      // Draw the cropped signature at 1:1 scale (no scaling)
      ctx.drawImage(img, minX, minY, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight)

      // Convert back to data URL
      resolve(canvas.toDataURL('image/png'))
    }
    img.src = dataUrl
  })
}

onMounted(() => {
  // Ensure the signature pad is properly sized
  window.addEventListener('resize', () => {
    signaturePad.value?.resizeCanvas()
  })
})
</script>

<style scoped>
.signature-capture-container {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 1rem;
  overflow-y: auto;
}

.signature-header {
  text-align: center;
  margin-bottom: 1rem;
}

.signature-header h2 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #666;
  margin-top: 0.5rem;
}

/* Legal declaration styling */
.legal-declaration {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

/* Dark mode for legal declaration */
@media (prefers-color-scheme: dark) {
  .legal-declaration {
    background-color: #1f2937;
    border-color: #374151;
    color: #f9fafb;
  }
}

.declaration-text {
  font-size: 1rem;
  font-weight: bold;
  font-style: italic;
  line-height: 1.5;
  white-space: pre-line;
  margin-bottom: 1rem;
}

.agreement-text {
  font-size: 0.9rem;
  font-style: italic;
  color: #666;
}

@media (prefers-color-scheme: dark) {
  .agreement-text {
    color: #9ca3af;
  }
}

.sign-here-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.sign-here-text {
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

/* Dark mode for SIGN HERE text */
@media (prefers-color-scheme: dark) {
  .sign-here-text {
    color: #f9fafb;
  }
}

.chevron-icon {
  font-size: 1rem;
  color: #666;
  animation: bounce 2s infinite;
}

.signature-pad-container {
  flex: 1;
  max-height: 300px;
  border: 1px solid #ddd;
  outline: 2px solid rgba(0, 109, 0, 0.581);
  border-radius: 4px;
  background-color: rgb(248, 249, 250);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signature-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.action-left {
  flex: 1;
}

.action-right {
  display: flex;
  gap: 0.5rem;
}

/* Fullscreen mode styles */
@media screen and (max-width: 768px) {
  .signature-capture-container {
    padding: 0.5rem;
  }

  .signature-pad-container {
    border-radius: 0;
  }

  .legal-declaration {
    padding: 0.75rem;
  }

  .declaration-text {
    font-size: 0.9rem;
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}
</style>
